import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import WeightMeasuresSettings, { WeightMeasureSlider } from './WeightMeasuresSettings';
import PercentageFitCircle from '../PercentageFitCircle';
import { AllTShirtMeasuresMandatory } from '../../types/databaseTables/idealteemeasures';
import { idealGarmentMeasurementsZero } from '../../tools/generalProperties';
import { LoggingContext } from '../../Context/LoggingContext/LoggingContext';

function HelpSection({
  setToggleMoreInfoState,
}: {
  setToggleMoreInfoState: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [demoSliderValue, setDemoSliderValue] = React.useState<AllTShirtMeasuresMandatory>({
    ...idealGarmentMeasurementsZero,
    sleevelength: 3,
  });

  return (
    <Box sx={{ margin: '24px 24px' }}>
      <p>
        Garments are sorted according to those which <b>fit</b> you best.
      </p>
      <p>
        The <b>fit</b> and <b>reliability</b> scores are calculated by comparing your own{' '}
        <b>Ideal</b> garment measurements with each of the garment&apos;s measurements.
      </p>
      <p>
        If some measurements haven&apos;t been saved on the garment, then the <b>reliability</b>{' '}
        score will be reduced.
      </p>
      <Grid container justifyContent='space-around' direction='row'>
        <PercentageFitCircle title='reliability' value={50} />
      </Grid>
      <p>
        The closer that your <b>ideal</b> measurement is to the garment measurement, the higher the{' '}
        <b>fit</b> score will be.
      </p>
      <Grid container justifyContent='space-around' direction='row'>
        <PercentageFitCircle title='fit' value={95} />
      </Grid>
      <p>
        You can adjust the weight of each measurement with these sliders. The higher the weight
        value, the greater the effect the measurement will have on the fit calculation.
      </p>
      <WeightMeasureSlider
        value={demoSliderValue.sleevelength || 0}
        setValue={setDemoSliderValue}
        sliderLabel='sleevelength'
      />
      <p>
        If you reduce a slider to zero, then that measurement will have no effect on the <b>fit</b>{' '}
        score. For example, if sleeve size is unimportant to you, then reducing the{' '}
        <b>Sleeve Length</b> slider to zero will mean that the sorting calculation completely
        ignores <b>Sleeve Length</b>.
      </p>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <IconButton
          aria-label='back to settings'
          color='primary'
          onClick={() => setToggleMoreInfoState(true)}
        >
          <SettingsIcon />
        </IconButton>
      </Box>
      <p style={{ textAlign: 'center' }}>Click the settings Icon to go to the sliders.</p>
    </Box>
  );
}

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction='up' ref={ref} {...props} />
);

export default function WeightMeasuresDialog({
  weightMeasureSettingsDialogOpenState,
  setWeightMeasureSettingsDialogOpenState,
}: {
  weightMeasureSettingsDialogOpenState: boolean;
  setWeightMeasureSettingsDialogOpenState: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const theme = useTheme();
  const location = useLocation();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { addLog } = React.useContext(LoggingContext);
  const [toggleMoreInfoState, setToggleMoreInfoState] = React.useState(false);

  const handleClose = () => {
    setWeightMeasureSettingsDialogOpenState(false);
    addLog('weight measure settings closed', location.pathname);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={weightMeasureSettingsDialogOpenState}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge='start' color='inherit' onClick={handleClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
            Measure Weighting Adjustments
          </Typography>
          {!toggleMoreInfoState ? (
            <IconButton
              aria-label='back to settings'
              color='inherit'
              onClick={() => setToggleMoreInfoState(true)}
            >
              <SettingsIcon />
            </IconButton>
          ) : (
            <IconButton
              aria-label='more information'
              color='inherit'
              onClick={() => setToggleMoreInfoState(false)}
            >
              <HelpOutlineIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      {/* Dialog Contents */}
      <Box
        sx={{
          width: { sm: '550px' },
        }}
      >
        {!toggleMoreInfoState ? (
          <HelpSection setToggleMoreInfoState={setToggleMoreInfoState} />
        ) : (
          <WeightMeasuresSettings />
        )}
      </Box>
    </Dialog>
  );
}
